import { BlogPost } from "./types";

export const comprimirFotoParaCv: BlogPost = {
  slug: "como-comprimir-foto-para-cv",
  title: "Cómo comprimir una foto para CV: tamaños, formatos y cuándo incluirla",
  description: "Guía completa para preparar la foto de tu currículum. Dimensiones recomendadas, peso del archivo, si incluirla o no, y cómo integrarla correctamente en PDF y Word.",
  date: "2026-03-24",
  readTime: "8 min",
  keywords: "comprimir foto cv, foto curriculum, tamaño foto curriculum vitae, foto cv formato, imagen curriculum profesional",
  content: `
## ¿Deberías incluir foto en tu CV?

Antes de hablar de cómo comprimir la foto, es relevante saber cuándo incluirla.

**Contextos donde la foto es esperada o recomendada:**
- Mercado laboral español, latinoamericano y europeo continental (Alemania, Francia, Italia)
- Posiciones donde la presencia o comunicación interpersonal es relevante (ventas, atención al cliente, hostelería, relaciones públicas)
- CVs enviados directamente a empresas (sin intermediarios)

**Contextos donde la foto es innecesaria o puede perjudicarte:**
- Mercado laboral anglosajón (Reino Unido, EE.UU., Canadá, Australia) — la foto puede activar sesgos y los reclutadores la evitan activamente
- Candidaturas a través de plataformas ATS (Applicant Tracking Systems) — muchos sistemas no procesan las imágenes correctamente
- Posiciones técnicas donde el portfolio o habilidades dominan la selección

Para el mercado español actual (2024-2026), incluir foto sigue siendo práctica habitual y generalmente esperada salvo indicación contraria.

## Especificaciones técnicas de la foto para CV

### Dimensiones recomendadas

| Uso del CV | Dimensiones de la foto | Resolución |
|---|---|---|
| CV en papel (imprimible) | 35×45 mm a 300ppi = 413×531 px | 300 DPI |
| CV enviado como PDF digital | 200×260 px o 150×190 px | 96-150 DPI |
| CV con diseño moderno (foto circular) | 300×300 px | 96-150 DPI |
| Perfil en LinkedIn (diferente) | 400×400 px | 96 DPI |

Para CVs que se van a enviar principalmente en formato digital (email, plataformas), las dimensiones de 150-200px de ancho son suficientes. La foto en el CV es pequeña — generalmente 2-4 cm en el documento físico.

### Peso del archivo de la foto

Aunque el archivo de la foto en sí puede ser de cualquier tamaño si la insertas en Word (que lo gestionan internamente), el peso total del CV en PDF no debería superar:
- **CV estándar:** 1-3 MB
- **CV con portfolio visual:** hasta 5 MB
- **Límite para envío por email:** bajo 10 MB

Una foto de CV bien comprimida pesa entre 30-100KB. Esto es una pequeña fracción del total del PDF.

### Formato de la foto

- **JPEG 85-90%:** la mejor opción para fotos de retrato. Buen balance de calidad y tamaño.
- **PNG:** innecesario para fotos de perfil. Más grande sin ventaja visible para fotografías.
- **WebP:** no recomendado para insertar en Word/PDF por compatibilidad variable.

## Cómo hacer una buena foto de CV

### Características de la foto ideal

1. **Fondo neutro:** blanco, gris claro, o color corporativo suave. Evita fondos recargados o exteriores con mucho detalle.

2. **Ropa profesional:** acorde con el sector. Un traje para finanzas o derecho; business casual para tecnología o marketing.

3. **Expresión amigable y profesional:** sonrisa moderada o expresión neutra con mirada directa a la cámara.

4. **Encuadre:** de hombros arriba (medio busto). Deja espacio en la parte superior de la cabeza.

5. **Iluminación:** natural y uniforme. Evita fondos con ventanas brillantes detrás, o luz directa que cree sombras duras.

6. **Nitidez:** la imagen debe estar en foco. Usa el modo retrato del móvil para desenfocar el fondo si lo deseas, pero asegúrate de que la cara esté perfectamente nítida.

### Cómo hacerla con el móvil

Sin necesidad de fotógrafo profesional:
1. Busca buena luz natural, idealmente frente a una ventana (no con la ventana detrás)
2. Coloca una pared lisa o sábana blanca como fondo
3. Pide a alguien que te haga la foto a la altura de los ojos
4. Haz 10-15 fotos y elige la mejor
5. Recorta para dejar la proporción correcta (más alto que ancho)

## Proceso de compresión paso a paso

### Para CV digital (PDF enviado por email)

1. **Recorta la foto:** encuadra desde poco más arriba de la coronilla hasta el pecho. Proporción vertical (más alto que ancho).

2. **Redimensiona:** si la foto es de móvil o cámara (varios MB), redimensiona a 200-300px de ancho usando nuestro [compresor de imágenes](/).

3. **Comprime:** calidad JPEG 85-90%. Una foto de 200×260px al 90% pesará entre 20-50KB.

4. **Verifica:** abre la imagen y comprueba que se ve bien y la cara está nítida.

### Para CV en Word

1. Inserta la foto en Word (Insertar → Imágenes)
2. Redimensiona en Word al tamaño deseado
3. Haz clic derecho en la imagen → Comprimir imágenes → Selecciona "Correo electrónico (96 ppi)"
4. Al exportar a PDF (Archivo → Guardar como PDF), Word aplica su propia optimización

Alternativa: comprime la foto previamente con nuestro compresor y luego insértala en Word ya optimizada. Esto da más control sobre el resultado final.

### Para CV en PDF (Canva, Adobe InDesign, etc.)

- **Canva:** sube la foto ya redimensionada y comprimida (200×260px). Canva aplica su propia compresión al exportar.
- **Adobe InDesign:** usa "Guardar para web" o exporta el PDF con perfil "Más pequeño".
- **Google Docs:** inserta la imagen optimizada — Google Docs puede distorsionar mucho las imágenes si no están pre-optimizadas.

## Diseños de CV con foto: opciones habituales

### Foto cuadrada o rectangular en la esquina

El posicionamiento más clásico y profesional. La foto suele medir 3-4 cm en el documento y va en la esquina junto al nombre y datos de contacto.

- Dimensiones de la foto: 150-200px de ancho
- Proporción: 1:1 (cuadrada) o 3:4 (rectangular, más carnet)

### Foto circular en diseños modernos

Los CVs de diseño moderno con plantillas de Canva o similares suelen usar foto circular. Para estos diseños:
- La foto debe ser cuadrada para que el recorte circular sea simétrico
- Dimensiones: 300×300 px
- Asegúrate de que la cara quede centrada cuando se aplique el recorte circular

## Aspectos de privacidad: RGPD

En Europa, el Reglamento General de Protección de Datos (RGPD) se aplica también a los CVs. Las empresas deben gestionar los datos de los candidatos con responsabilidad.

Aspectos relevantes:
- Una foto es un dato biométrico según el RGPD
- Tienes derecho a no incluirla si no la consideras necesaria
- Si la envías digitalmente, la empresa debe gestionarla conforme al RGPD

Para el candidato: incluye la foto cuando te aporte más de lo que te puede perjudicar, y asegúrate de que es una foto profesional y actual.

## Preguntas frecuentes

### ¿Qué tamaño exacto debe tener la foto en el CV?

En el documento final (papel o PDF), la foto ocupa habitualmente entre 2.5×3 cm y 4×5 cm. Para que se vea nítida a ese tamaño:
- Digital: 150×200px (suficiente para pantalla)
- Impresión: 295×390px (suficiente para 2.5×3 cm a 300 DPI)

Una foto de 200×260px cubre ambos casos sin ser innecesariamente pesada.

### ¿La foto del CV debe ser en color o en blanco y negro?

En color. Los CVs en blanco y negro son inusuales en el mercado actual y pueden dar apariencia de documento desactualizado.

### ¿Con qué frecuencia debo actualizar la foto del CV?

Cada 2-3 años, o cuando tu apariencia cambie significativamente. Una foto de hace 10 años puede crear discordancia en la entrevista presencial.

### ¿Puedo usar la misma foto del CV para LinkedIn?

Sí, pero LinkedIn recomienda fotos cuadradas de al menos 400×400px. Si tu foto de CV es rectangular, necesitarás recortarla a cuadrado para LinkedIn.

### ¿El sistema ATS lee la foto del CV?

Los sistemas ATS modernos generalmente ignoran las imágenes o no las procesan. La importancia de la foto está en la fase de revisión humana del CV, no en el filtrado automático.

## Conclusión

La foto de CV ideal tiene 150-300px de ancho, proporción vertical (3:4), calidad JPEG 85-90%, y pesa entre 25-80KB. Estas especificaciones producen una foto que se ve perfectamente en cualquier pantalla y en papel, sin añadir peso innecesario al PDF final.

El proceso: recorta a proporción vertical con la cabeza y hombros bien encuadrados, redimensiona a 200px de ancho aproximadamente, comprime al 85% JPEG con nuestro [compresor de imágenes](/), e inserta en tu CV.
  `
};
